import React, { forwardRef, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { IconButton, InputBase, Paper } from "@mui/material";
import { htmlValidation } from "../../helpers";
import SearchIcon from "@mui/icons-material/Search";
import { styled } from "@mui/material/styles";

type FTPSearchBarType = {
  onSearch: (query: string) => void;
  placeHolder: string;
  clientSideSearch?: boolean;
};

// Styled components
const StyledIconButton = styled(IconButton)(({ theme }) => ({
  margin: "3px",
  border: `1px solid ${theme.palette.grey[200]}`,
  backgroundColor: theme.palette.grey[400],
  "&:hover": {
    backgroundColor: theme.palette.grey[500],
    color: theme.palette.common.white,
  },
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  flex: 1,
  fontSize: "0.95rem",
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  border: `3px solid ${theme.palette.grey[200]}`,
  backgroundColor: theme.palette.common.white,
  borderRadius: "35px",
  padding: "3px 10px",
  width: "400px",
  alignSelf: "center",
  [theme.breakpoints.down("md")]: {
    width: "100%",
    marginBottom: "20px",
  },
}));

export const FTPSearchBar = observer(
  forwardRef<HTMLInputElement, FTPSearchBarType>((props, ref) => {
    const { onSearch, placeHolder, clientSideSearch } = props;
    const queryRef = useRef<HTMLInputElement | null>(null);
    const [query, setQuery] = useState("");

    const onKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        htmlValidation(event);
        onSearch(query.trim());
      }
    };

    const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (clientSideSearch) {
        setQuery(value);
        onSearch(value.trim());
      } else {
        setQuery(value);
      }
    };

    return (
      <StyledPaper>
        <StyledInputBase
          inputRef={queryRef}
          placeholder={placeHolder}
          value={query}
          onChange={onChangeHandler}
          onKeyDown={onKeyDownHandler}
          inputProps={{ "aria-label": "search" }}
        />
        <StyledIconButton
          type="button"
          aria-label="search"
          onClick={() => onSearch(query.trim())}
        >
          <SearchIcon />
        </StyledIconButton>
      </StyledPaper>
    );
  }),
);
